/*
 * Purpose: Built-in AutoBlocks connectors — owned capability library (SSOT)
 * Last modified: 2026-06-25 | Agent
 * Completeness: 88
 */
import type { AutoBlocksConnector } from './types';

const ts = () => new Date().toISOString();

export const BUILTIN_CONNECTORS: AutoBlocksConnector[] = [
  {
    id: 'ab.gmail',
    name: 'Gmail',
    vendor: 'autoblocks',
    category: 'email',
    description: 'Watch inbox via AutoBlocks edge inbox or native Gmail API OAuth.',
    auth: 'oauth2',
    patternRef: 'email-watch-ingest',
    runtime: 'hybrid',
    status: 'beta',
    version: 2,
    createdAt: ts(),
    capabilities: [
      {
        id: 'gmail.inbound',
        kind: 'trigger',
        label: 'Email received',
        blockType: 'trigger',
        blockSubtype: 'gmail_inbound',
        description: 'Poll edge inbox or Gmail API for matching messages.',
      },
      {
        id: 'gmail.parse',
        kind: 'tool',
        label: 'Parse email body',
        blockType: 'tool',
        blockSubtype: 'transform',
        description: 'Extract roles, amounts, dates from email payload.',
      },
    ],
    setupSteps: [
      'AutoBlocks creates an edge inbox (or use Connect Gmail in the compose review).',
      'For API mode: Connect Gmail — token stored in Secrets as GOOGLE_ACCESS_TOKEN.',
      'Set Gmail search query on the trigger node (e.g. from:linkedin.com subject:job).',
    ],
  },
  {
    id: 'ab.openai',
    name: 'OpenAI',
    vendor: 'autoblocks',
    category: 'ai',
    description: 'Chat completions for tailoring text, summarization, classification.',
    auth: 'secret',
    runtime: 'client',
    status: 'stable',
    version: 1,
    createdAt: ts(),
    capabilities: [
      {
        id: 'openai.complete',
        kind: 'tool',
        label: 'AI completion',
        blockType: 'tool',
        blockSubtype: 'http',
        description: 'POST to OpenAI chat completions with fact-only prompts.',
      },
    ],
    setupSteps: ['Add OPENAI_API_KEY to Secrets.'],
  },
  {
    id: 'ab.linkedin',
    name: 'LinkedIn',
    vendor: 'autoblocks',
    category: 'jobs',
    description: 'Member sign-in (OIDC) for profile context and assisted job flows.',
    auth: 'oauth2',
    runtime: 'client',
    status: 'beta',
    version: 1,
    createdAt: ts(),
    capabilities: [
      {
        id: 'linkedin.profile',
        kind: 'tool',
        label: 'Member profile',
        blockType: 'tool',
        blockSubtype: 'http',
        description: 'OpenID Connect profile for resume tailoring.',
      },
    ],
    setupSteps: [
      'Create app at linkedin.com/developers — request Sign In with LinkedIn (OIDC).',
      'Redirect URL: http://localhost:3000/oauth/linkedin/callback',
      'Save LINKEDIN_CLIENT_ID and LINKEDIN_CLIENT_SECRET in Secrets, then Connect LinkedIn.',
      'Token stored as LINKEDIN_TOKEN for HTTP blocks.',
    ],
  },
  {
    id: 'ab.http',
    name: 'HTTP API',
    vendor: 'autoblocks',
    category: 'integration',
    description: 'Call any REST API with secret substitution in headers.',
    auth: 'api_key',
    runtime: 'client',
    status: 'stable',
    version: 1,
    createdAt: ts(),
    capabilities: [
      {
        id: 'http.request',
        kind: 'tool',
        label: 'HTTP request',
        blockType: 'tool',
        blockSubtype: 'http',
        description: 'GET/POST/PATCH external APIs.',
      },
      {
        id: 'http.webhook_out',
        kind: 'action',
        label: 'POST webhook',
        blockType: 'action',
        blockSubtype: 'webhook',
        description: 'Send workflow output to an external URL.',
      },
    ],
    setupSteps: ['Configure URL and optional $SECRET headers on the HTTP block.'],
  },
  {
    id: 'ab.storage',
    name: 'AutoBlocks Storage',
    vendor: 'autoblocks',
    category: 'data',
    description: 'Persist records locally (tracker, logs) with named keys.',
    auth: 'none',
    runtime: 'client',
    status: 'stable',
    version: 1,
    createdAt: ts(),
    capabilities: [
      {
        id: 'storage.write',
        kind: 'action',
        label: 'Store record',
        blockType: 'action',
        blockSubtype: 'storage',
        description: 'Save JSON to browser storage under a key.',
      },
    ],
    setupSteps: ['Set storage key on the Store Data action node.'],
  },
  {
    id: 'ab.notify',
    name: 'Notifications',
    vendor: 'autoblocks',
    category: 'alerts',
    description: 'In-app toasts and console alerts when steps complete.',
    auth: 'none',
    runtime: 'client',
    status: 'stable',
    version: 1,
    createdAt: ts(),
    capabilities: [
      {
        id: 'notify.toast',
        kind: 'action',
        label: 'Notify user',
        blockType: 'action',
        blockSubtype: 'notification',
        description: 'Show completion or alert message.',
      },
      {
        id: 'notify.log',
        kind: 'action',
        label: 'Log output',
        blockType: 'action',
        blockSubtype: 'log',
        description: 'Write step output to execution log.',
      },
    ],
    setupSteps: ['Set message text on the notification node.'],
  },
  {
    id: 'ab.scheduler',
    name: 'Scheduler',
    vendor: 'autoblocks',
    category: 'time',
    description: 'Interval and delayed steps (client timer; edge cron forthcoming).',
    auth: 'none',
    runtime: 'hybrid',
    status: 'scaffold',
    version: 1,
    createdAt: ts(),
    capabilities: [
      {
        id: 'time.timer',
        kind: 'trigger',
        label: 'Timer',
        blockType: 'trigger',
        blockSubtype: 'timer',
        description: 'Run on interval.',
      },
      {
        id: 'time.delay',
        kind: 'tool',
        label: 'Delay',
        blockType: 'tool',
        blockSubtype: 'delay',
        description: 'Wait between steps.',
      },
    ],
    setupSteps: ['Set interval seconds on timer or delay duration on delay node.'],
  },
  {
    id: 'ab.logic',
    name: 'Flow logic',
    vendor: 'autoblocks',
    category: 'control',
    description: 'Branching, transforms, and conditions.',
    auth: 'none',
    runtime: 'client',
    status: 'stable',
    version: 1,
    createdAt: ts(),
    capabilities: [
      {
        id: 'logic.transform',
        kind: 'tool',
        label: 'Transform',
        blockType: 'tool',
        blockSubtype: 'transform',
        description: 'Map, score, reshape data.',
      },
      {
        id: 'logic.branch',
        kind: 'tool',
        label: 'Conditional',
        blockType: 'tool',
        blockSubtype: 'conditional',
        description: 'Continue only when condition passes.',
      },
    ],
    setupSteps: ['Edit transform script or condition expression on the node.'],
  },
];

export function getBuiltinConnector(id: string): AutoBlocksConnector | undefined {
  return BUILTIN_CONNECTORS.find((c) => c.id === id);
}
